import { Details, AssessmentTabs, Scorecard } from '..';

import { Button, Flex, Icon, useDisclosure } from '@chakra-ui/react';
import { useMemo, useRef, useState } from 'react';
import { CenterSpinner } from '@/components/ats';
import { AutomationStopConfirmDialog } from '@/components/automation/AutomationStopConfirmDialog';
import { useShowToast } from '@/hooks';
import { KDrawer } from '@/ui-library/components/molecules/KDrawer';
import { KText } from '@/ui-library/components/text';
import { useMutation, useQuery } from '@tanstack/react-query';
import { ASSESSMENT_API } from '../api/codingAssessment.api';
import { getErrorMessage } from '@/utils/errors';
import {
  Hackerrank,
  Hackerearth,
  CriteriaCorp,
  Hireflix,
  CodeInterview as CodeInterviewIcon,
} from '@/assets/icons/ats';
import type { PlatformLogical } from '@/types/ext_assessment';

const PLATFORM_CONFIG = {
  hackerrank: {
    name: 'HackerRank',
    icon: Hackerrank,
    apiKey: 'hackerrank',
  },
  hackerearth: {
    name: 'HackerEarth',
    icon: Hackerearth,
    apiKey: 'hackerearth',
  },
  criteriacorp: {
    name: 'Criteria Corp',
    icon: CriteriaCorp,
    apiKey: 'criteriacorp',
  },
  hireflix: {
    name: 'Hireflix',
    icon: Hireflix,
    apiKey: 'hireflix',
  },
  codeinterview: {
    name: 'CodeInterview',
    icon: CodeInterviewIcon,
    apiKey: 'codeinterview',
  },
};

interface AssessmentDrawerProps {
  isOpen?: boolean;
  onClose?: () => void;
  activity?: any;
  application?: any;
  stageId?: string;
  refetch?: () => void;
  isAdhoc?: boolean;
  platform: PlatformLogical;
  /** True when saving with the given scorecard_required value would stop this
   *  candidate's stage automation. Computed by the caller from the stage's
   *  activity list. Mirrors the Kula assessment flow. */
  wouldStopAutomation?: (_scorecardRequired: boolean) => boolean;
  /** Cancels the stranded stage-automation executions after a successful save.
   *  No-op unless the scorecard was turned optional with a dependent downstream
   *  activity. */
  onScorecardSaved?: (_scorecardRequired: boolean) => void | Promise<void>;
}

export const CodingAssessmentDrawer: React.FC<AssessmentDrawerProps> = ({
  isOpen = true,
  onClose = () => {},
  activity,
  application,
  stageId,
  refetch = () => {},
  isAdhoc = false,
  platform,
  wouldStopAutomation,
  onScorecardSaved,
}) => {
  const platformConfig = PLATFORM_CONFIG[platform];
  const toast = useShowToast();

  // Confirm shown before an edit that turns the scorecard optional while a later
  // automated activity is gated on its submission — saving stops that automation
  // for this candidate. `automationConfirmedRef` lets the confirm re-enter
  // `handleSubmit` past the guard; `pendingCancelRef`/`nextScorecardRequiredRef`
  // carry the decision into the mutation's async success handler.
  const automationStopConfirmActions = useDisclosure();
  const automationConfirmedRef = useRef(false);
  const pendingCancelRef = useRef(false);
  const nextScorecardRequiredRef = useRef(true);

  const { data: createData, isLoading: isCreateLoading } = useQuery({
    queryKey: ['createAssessment', activity?.id, application?.id, platform],
    queryFn: () =>
      ASSESSMENT_API.createAssessment({
        ats_job_stage_action_id: activity?.id,
        ats_application_id: application?.id,
        platform,
      }),
    enabled: !!(activity?.id && application?.id && !activity?.data?.id),
    staleTime: 0,
  });

  const assessmentId = activity?.data?.id || createData?.id;

  const { data: assessmentData, isLoading: isGetLoading } = useQuery({
    queryKey: ['getAssessment', assessmentId, platform],
    queryFn: () => ASSESSMENT_API.getAssessment(assessmentId, platform),
    enabled: !!assessmentId,
    staleTime: 0,
  });

  const inviteMode = useMemo(
    () =>
      activity?.data?.status === 'pending' ||
      assessmentData?.status === 'pending',
    [activity?.data?.status, assessmentData?.status]
  );

  const inviteAssessmentMutation = useMutation({
    mutationFn: ASSESSMENT_API.inviteAssessment,
    onSuccess: async (result) => {
      // @ts-expect-error API response type lacks data.id in generated types
      if (result?.data?.id) {
        toast({
          title: inviteMode
            ? `${platformConfig.name} assessment sent successfully`
            : `${platformConfig.name} assessment updated successfully`,
          status: 'success',
        });
        // Stop the candidate's stage automation when this edit turned the
        // scorecard optional (awaited before refetch so a single refetch
        // reflects both). No-op otherwise.
        if (pendingCancelRef.current) {
          pendingCancelRef.current = false;
          await onScorecardSaved?.(nextScorecardRequiredRef.current);
        }
        refetch();
        onClose();
      } else {
        toast({
          title: getErrorMessage(result, 'extAssessment'),
          status: 'error',
        });
        onClose();
      }
    },
    onError: (error) => {
      toast({
        title: getErrorMessage(error, 'extAssessment'),
        status: 'error',
      });
      onClose();
    },
  });

  const inviteAdhocAssessmentMutation = useMutation({
    mutationFn: ASSESSMENT_API.inviteAdhocAssessment,
    onSuccess: (result) => {
      // @ts-expect-error API response type lacks success field in generated types
      if (result?.success) {
        toast({
          title: `${platformConfig.name} assessment sent successfully`,
          status: 'success',
        });
        refetch();
        onClose();
      } else {
        toast({
          title: getErrorMessage(result, 'extAssessment'),
          status: 'error',
        });
        onClose();
      }
    },
    onError: (error) => {
      toast({
        title: getErrorMessage(error, 'extAssessment'),
        status: 'error',
      });
      onClose();
    },
  });

  const [currentTab, setCurrentTab] = useState('details');
  const isLoading = isCreateLoading || isGetLoading;

  const detailsRef = useRef(null);
  const scorecardRef = useRef(null);

  const assessmentTabs = [
    {
      id: 'details',
      label: 'Details',
      component: Details,
      props: {
        formRef: detailsRef,
        activity: assessmentData || {},
        jobStage: true,
        platform,
      },
    },
    {
      id: 'scorecard',
      label: 'Scorecard',
      component: Scorecard,
      props: {
        ref: scorecardRef,
        note: 'This is the recommended scorecard template. You can customize it but changes will only apply to this assessment.',
        interviewPlan: {
          id: assessmentData?.id,
          scorecard_config: assessmentData?.scorecard,
          scorecard_required:
            assessmentData?.scorecard_required ?? activity?.scorecard_required,
          ...assessmentData?.scorecard,
        },
        platform,
      },
    },
  ];

  const getNextTab = () => {
    const currentIndex = assessmentTabs.findIndex(
      (tab) => tab.id === currentTab
    );
    return assessmentTabs?.[currentIndex + 1];
  };

  const handleSubmit = async () => {
    if (currentTab === 'details') {
      await detailsRef?.current?.submitForm();
      if (!detailsRef?.current?.isValid) return;

      setCurrentTab('scorecard');
    } else if (currentTab === 'scorecard') {
      const scorecardResult = await scorecardRef?.current?.getModel();

      if (!scorecardResult?.hasValidData) {
        return;
      }

      const detailsValues = detailsRef?.current?.values || {};
      const scorecardPayload = scorecardResult?.payload || {};
      const {
        scorecard_feedback_description,
        scorecard_rating_description,
        scorecard_config,
        scorecard_required = true,
      } = scorecardPayload;

      const finalParams = {
        ext_test_name: assessmentData?.ext_test_name,
        ext_assessment_link: assessmentData?.ext_assessment_link,
        ...detailsValues,
        feedback_description: scorecard_feedback_description,
        rating_description: scorecard_rating_description,
        scoreCards: scorecard_config?.questions,
        scorecard_required,
        platform,
        kind:
          platform === 'hackerearth'
            ? 'hackerearth_assessment'
            : platform === 'codeinterview'
              ? 'codeinterview_assessment'
              : platform,
      };

      // Confirm only when THIS edit turns the scorecard optional (a real toggle
      // change) AND a later automated activity depends on its submission — an
      // unchanged scorecard (or a fresh send) saves silently. Only the update
      // path can strand an existing execution, so gate on `!isAdhoc && !inviteMode`.
      //
      // `scorecard_required` ships as the STRING 'true'|'false' from the
      // scorecard builder (ENG-3207 — the BE coerces a boolean false to true),
      // so normalise both original + next to booleans before the automation
      // check (mirrors `parseScorecardRequired` in the scorecard container).
      const toScorecardBool = (value: unknown): boolean =>
        value !== false && value !== 'false';
      const originalScorecardRequired = toScorecardBool(
        assessmentData?.scorecard_required ?? activity?.scorecard_required
      );
      const nextScorecardRequired = toScorecardBool(scorecard_required);
      const willStopAutomation =
        !isAdhoc &&
        !inviteMode &&
        originalScorecardRequired &&
        !nextScorecardRequired &&
        Boolean(wouldStopAutomation?.(nextScorecardRequired));

      if (willStopAutomation && !automationConfirmedRef.current) {
        automationStopConfirmActions.onOpen();
        return;
      }
      // Consume the one-shot confirm and carry the cancel decision to onSuccess.
      pendingCancelRef.current = willStopAutomation;
      nextScorecardRequiredRef.current = nextScorecardRequired;
      automationConfirmedRef.current = false;

      if (isAdhoc) {
        inviteAdhocAssessmentMutation.mutate({
          assessmentId: activity?.data?.id || createData?.id,
          ats_job_stage_id: stageId,
          ats_job_stage_action_id: activity?.id,
          ats_application_id: application?.id,
          params: finalParams,
          platform,
        });
      } else {
        inviteAssessmentMutation.mutate({
          assessmentId: activity?.data?.id || createData?.id,
          ats_job_stage_id: stageId,
          ats_job_stage_action_id: activity?.id,
          ats_application_id: application?.id,
          params: finalParams,
          platform,
        });
      }
    }
  };

  // Automation-stop confirm CTA — re-run the save with the guard satisfied.
  const handleAutomationStopConfirm = async () => {
    automationStopConfirmActions.onClose();
    automationConfirmedRef.current = true;
    await handleSubmit();
  };

  const renderFooter = () => {
    if (isLoading) return null;
    return (
      <Flex
        p="lg"
        borderTop="1px solid"
        borderColor="border-color"
        bg="ats-secondary"
      >
        {currentTab === 'scorecard' && (
          <Button variant="secondary" onClick={() => setCurrentTab('details')}>
            Back
          </Button>
        )}
        <Flex ml="auto" gap="12px">
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>

          <Button
            variant="primary"
            onClick={handleSubmit}
            isLoading={
              inviteAssessmentMutation.isPending ||
              inviteAdhocAssessmentMutation.isPending
            }
          >
            {currentTab !== 'scorecard'
              ? `Next: ${getNextTab()?.label} `
              : inviteMode || isAdhoc
                ? 'Send assessment'
                : 'Update assessment'}
          </Button>
        </Flex>
      </Flex>
    );
  };

  const renderHeader = () => {
    if (isLoading) return null;
    return (
      <Flex p="20px 24px 0px 24px">
        <KText size="md-medium">
          {isAdhoc || inviteMode ? 'Send' : 'Update'}
        </KText>
        <Icon
          as={platformConfig.icon}
          w="22px"
          h="22px"
          borderRadius="sm"
          mx="5px"
        />
        <KText size="md-medium">{platformConfig.name} assessment</KText>
      </Flex>
    );
  };

  return (
    <>
      <KDrawer
        isOpen={isOpen}
        onClose={onClose}
        footer={renderFooter()}
        header={renderHeader()}
        contentProps={{
          maxW: '700px',
          borderTopLeftRadius: 'xl',
        }}
      >
        <Flex flexDir="column" minH="100%">
          {isLoading ? (
            <CenterSpinner flex="1" />
          ) : (
            <AssessmentTabs
              tabs={assessmentTabs}
              isCreateFlow={false}
              currentTab={currentTab}
              activity={assessmentData}
              onTabChange={setCurrentTab}
              platform={platform}
              onTabValidation={async (fromTab, toTab) => {
                if (fromTab === 'details' && toTab === 'scorecard') {
                  await detailsRef?.current?.submitForm();
                  return detailsRef?.current?.isValid;
                }
                return true;
              }}
            />
          )}
        </Flex>
      </KDrawer>
      <AutomationStopConfirmDialog
        isOpen={Boolean(automationStopConfirmActions?.isOpen)}
        activity="assessment"
        scope="candidate"
        onConfirm={handleAutomationStopConfirm}
        onCancel={() => automationStopConfirmActions?.onClose()}
        isLoading={inviteAssessmentMutation.isPending}
      />
    </>
  );
};
